import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MediaPreview } from './MediaPreview';

interface Props {
  urls: string[];
  alt?: string;
  /** Cover for a video (the first slide only). */
  poster?: string | null;
  /** Classes for the media element itself (height, fit, background). */
  mediaClassName?: string;
  /** Colour of the dots and counter on themed client pages. */
  accent?: string;
}

/** One file, or a carousel with arrows, counter and dots. */
export function MediaCarousel({ urls, alt = '', poster, mediaClassName = '', accent = '#ffffff' }: Props) {
  const [index, setIndex] = useState(0);

  // A different item (or fewer files) shouldn't leave us past the end
  useEffect(() => {
    if (index > urls.length - 1) setIndex(0);
  }, [urls, index]);

  if (urls.length === 0) return null;
  const current = urls[Math.min(index, urls.length - 1)];
  const many = urls.length > 1;

  return (
    <div className="relative group">
      <MediaPreview
        key={current}
        url={current}
        alt={many ? `${alt} (${index + 1}/${urls.length})` : alt}
        controls
        poster={index === 0 ? poster : null}
        className={mediaClassName}
      />
      {many && (
        <>
          <button
            type="button"
            aria-label="Anterior"
            onClick={() => setIndex(i => (i - 1 + urls.length) % urls.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Seguinte"
            onClick={() => setIndex(i => (i + 1) % urls.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-semibold tabular-nums">
            {index + 1}/{urls.length}
          </span>
          <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1 pointer-events-none">
            {urls.map((u, i) => (
              <span
                key={`${u}-${i}`}
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: accent, opacity: i === index ? 1 : 0.4 }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
